/**
 * Reusable Navigation Link Components
 *
 * Centralized link configurations and button components for consistent
 * navigation across the homepage and other pages.
 */

import { Zap, Package, Activity, Bookmark } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

/**
 * Navigation link configuration
 */
export interface NavLinkConfig {
  href: string;
  label: string;
  shortLabel?: string;
  icon: LucideIcon;
  description: string;
  variant?: 'primary' | 'secondary' | 'tertiary';
}

/**
 * Primary navigation links used across the site
 * Single source of truth for navigation destinations
 */
export const PRIMARY_NAV_LINKS: NavLinkConfig[] = [
  {
    href: '/ai',
    label: 'Explore DCYFR AI',
    shortLabel: 'DCYFR AI',
    icon: Zap,
    description: 'Our open-source, multi-provider AI agent framework',
    variant: 'primary',
  },
  {
    href: '/open-source',
    label: 'Browse open source',
    shortLabel: 'Open Source',
    icon: Package,
    description: 'npm packages, the component registry, and plugins',
    variant: 'secondary',
  },
];

/**
 * Secondary/utility navigation links
 */
export const SECONDARY_NAV_LINKS: NavLinkConfig[] = [
  {
    href: '/activity',
    label: 'Activity',
    icon: Activity,
    description: 'See latest activity and updates',
  },
  {
    href: '/bookmarks',
    label: 'Bookmarks',
    icon: Bookmark,
    description: 'Curated resources and links',
  },
];

/**
 * All navigation links combined (for quick links ribbon)
 */
export const ALL_NAV_LINKS: NavLinkConfig[] = [...PRIMARY_NAV_LINKS, ...SECONDARY_NAV_LINKS];

/**
 * Get analytics source identifier for a link
 */
export function getAnalyticsSource(href: string, context: 'hero' | 'ribbon' | 'nav'): string {
  const page = href.replace('/', '') || 'home';
  return `homepage_${context}_${page}`;
}
